import { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill styles
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Box,
  Chip,
  Grid,
  Button,
  TextField,
  MenuItem,
  Stack,
  Paper,
  Dialog,
  Snackbar,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import LoadingScreen from "components/Loading";
import { getCompetencies, getTrainings } from "services/api";
import AddTrainingDialog from "components/AddTrainingDialog";
import MDBox from "components/MDBox";
import Fab from "@mui/material/Fab";
import Icon from "@mui/material/Icon";
import { addCompetencyToTraining, exportCompetencyToTraining } from "services/api";
import { deleteCompetencyToTraining } from "services/api";
import { useTranslation } from "react-i18next";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import PropTypes from "prop-types";
import ConfirmationDialog from "components/ConfirmationDialog";
import { CloudDownload } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import ImportButton from "components/ImportButton";

const CompetencyTrainingTab = ({ canEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [competencies, setCompetencies] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const snackbarRef = useRef();
  const [exportLoading, setExportLoading] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [confirmTrainingOpen, setConfirmTrainingOpen] = useState(false);
  const [selectedTrainingId, setSelectedTrainingId] = useState(null);

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  const handleAddTraining = async (data) => {
    const found = trainings?.some(
      (t) =>
        t.compId === data.compId &&
        t.trainingName?.trim().toLowerCase() === data?.trainingName?.trim().toLowerCase()
    );

    if (found) {
      snackbarRef.current?.showSnackbar(t("admin.training.add_duplicate"), "error");
      return;
    }
    // Call your API here to post the competency-training mapping
    try {
      // Call the addCompetencyToTraining function to save the mapping
      const response = await addCompetencyToTraining(data);
      setLoading(true);
      await loadTrainings();
      setLoading(false);

      snackbarRef.current?.showSnackbar(t("admin.training.add_success"), "success");

      // You can show a success message or perform other actions after saving
    } catch (error) {
      snackbarRef.current?.showSnackbar(t("admin.training.add_failed"), "error");
      // Handle any error hat occurs during the API call
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!loading) setLoading(true);

      const compData = await getCompetencies();
      setCompetencies(compData);
      await loadTrainings();
      // const jobG=await getJobGroupsByJobId(47);
    };

    fetchData().then((e) => {
      setLoading(false);
    });
  }, []);

  const loadTrainings = async () => {
    const trainingData = await getTrainings();
    if (trainingData != "error") {
      setTrainings(trainingData);
    } else {
      setTrainings([]);
    }
  };

  const handleDeleteTraining = async (id) => {
    setLoading(true);
    await deleteCompetencyToTraining(id);
    await loadTrainings();
    setLoading(false);
  };

  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <>
      <Box display="flex" justifyContent="flex-end" p={2} gap={2}>
        <ImportButton
          allowedTypes={["competencyToTraining"]}
          onError={handleImportError}
          disabled={!canEdit}
        />

        <MDButton
          variant="contained"
          color="primary"
          disabled={exportLoading}
          startIcon={<CloudDownload />}
          onClick={async () => {
            setExportLoading(true);
            try {
              const res = await exportCompetencyToTraining();
              if (res === "error") {
                console.error("Export failed");
              }
            } catch (e) {
              console.error(e);
            } finally {
              setExportLoading(false);
            }
          }}
        >
          {exportLoading ? <CircularProgress size={20} color="inherit" /> : t("common.export")}
        </MDButton>
      </Box>
      <Grid container spacing={2} p={2}>
        {trainings?.map((training) => {
          const competency = competencies?.find((c) => c.competencyId === training.compId);
          const competencyName = competency
            ? competency.name
            : t("admin.training.unknown_competency");

          return (
            <Grid item xs={12} sm={6} md={4} key={training.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6">
                    {training.trainingName || t("admin.training.unnamed_training")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("admin.competency.editDialog.competency_name")}: {competencyName}
                  </Typography>
                  {canEdit && (
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        // 🟢 Here is where the selected training is set
                        setSelectedTrainingId(training.id);
                        setConfirmTrainingOpen(true); // opens the confirmation dialog
                      }}
                      sx={{ position: "absolute", top: 8, right: 8, color: "red" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {canEdit && (
        <AddFloatingButton
          onClick={() => setOpenAddDialog(true)}
          icon="add"
          ariaLabel="Add Training Mapping"
        />
      )}

      <AddTrainingDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        competencies={competencies}
        handleSubmit={handleAddTraining}
      />
      <ConfirmationDialog
        open={confirmTrainingOpen}
        onClose={() => setConfirmTrainingOpen(false)}
        onConfirm={async () => {
          setConfirmTrainingOpen(false);
          if (selectedTrainingId) {
            await handleDeleteTraining(selectedTrainingId);
            setSelectedTrainingId(null);
          }
        }}
        title={t("admin.training.delete_confirmation_title") || "Delete Training?"}
        message={
          t("admin.training.delete_confirmation_message") ||
          "Are you sure you want to delete this training?"
        }
      />
      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </>
  );
};

CompetencyTrainingTab.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};
export default CompetencyTrainingTab;
